import React from "react";

const projects = [
  {
    name: "Board",
    desc: "A local-first Kanban board with drag-and-drop cards, checklists, due dates, a cross-board zen mode, and a command palette. Fully offline, data lives in IndexedDB.",
    tech: ["Svelte", "TypeScript", "Tailwind"],
    repo: "https://github.com/andyfransisko/task-board-web",
    demo: "https://task-board.andyfransisko.com",
  },
  {
    name: "SpendLog",
    desc: "A lightweight expense and income tracker with a running balance, category filters, and search. No accounts, your data stays in the browser.",
    tech: ["Svelte 5", "TypeScript", "Tailwind"],
    repo: "https://github.com/andyfransisko/money-spender-web",
    demo: "https://money-spender.andyfransisko.com",
  },
  {
    name: "MD Notes Editor",
    desc: "A markdown notes editor with write/preview tabs, search across notes, and a light/dark theme. Notes autosave locally and never leave the browser.",
    tech: ["React", "Vite", "Showdown"],
    repo: "https://github.com/andyfransisko/md-notes-editor-web",
    demo: "https://md-notes-editor.andyfransisko.com",
  },
  {
    name: "Screening Room",
    desc: "A minimal movie & show watchlist, add titles, mark them watched, rate them with stars, and filter your list. No account, no tracking.",
    tech: ["React 18", "Vite"],
    repo: "https://github.com/andyfransisko/movie-watchlist-web",
    demo: "https://movie-watchlist.andyfransisko.com",
  },
  {
    name: "Tournament Bracket Builder",
    desc: "Paste a list of participants and get a single-elimination bracket with bye handling, score entry, and automatic winner propagation to a champion banner.",
    tech: ["HTML", "CSS", "JavaScript"],
    repo: "https://github.com/andyfransisko/tournament-bracket-web",
    demo: "https://tournament-bracket.andyfransisko.com",
  },
  {
    name: "Loan EMI Calculator",
    desc: "Enter a loan amount, interest rate, and tenure to instantly see your monthly payment, total interest, and a principal vs. interest breakdown chart.",
    tech: ["React 18", "MUI 5", "Chart.js"],
    repo: "https://github.com/andyfransisko/loan-calculator-web",
    demo: "https://loan-calculator.andyfransisko.com",
  },
  {
    name: "Connect Four",
    desc: "A local two-player Connect Four game, take turns dropping discs and try to connect four in a row.",
    tech: ["React"],
    repo: "https://github.com/andyfransisko/connect-four-web",
    demo: "https://connect-four.andyfransisko.com",
  },
  {
    name: "Tally",
    desc: "A simple multi-counter app, create named counters, increment or decrement each one, and keep a running total. Autosaves to localStorage.",
    tech: ["HTML", "CSS", "JavaScript"],
    repo: "https://github.com/andyfransisko/simple-counter-web",
    demo: "https://simple-counter.andyfransisko.com",
  },
];

export default function Projects() {
  return (
    <section id="projects" className="py-10">
      <h3 className="font-bold text-3xl">
        My <span className="text-teal-500">Projects</span>
      </h3>
      <p className="gray-font mt-3">
        Some things I've built on my own time, every one runs live in the
        browser, so feel free to try them.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-12">
        {projects.map((project) => (
          <article
            key={project.name}
            className="about-bg rounded-xl p-6 flex flex-col border border-transparent hover:border-teal-500/40 hover:-translate-y-1 transition-all duration-200"
          >
            <div className="flex items-center justify-between mb-4">
              <i
                className="fa fa-folder-open-o text-3xl text-teal-500"
                aria-hidden="true"
              ></i>
              <div className="flex items-center gap-4">
                <a
                  href={project.repo}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${project.name} source code on GitHub`}
                  className="text-xl text-gray-400 hover:text-teal-400 transition-colors"
                >
                  <i className="fa fa-github" aria-hidden="true"></i>
                </a>
                <a
                  href={project.demo}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${project.name} live demo`}
                  className="text-lg text-gray-400 hover:text-teal-400 transition-colors"
                >
                  <i className="fa fa-external-link" aria-hidden="true"></i>
                </a>
              </div>
            </div>

            <h4 className="font-semibold text-lg">
              <a
                href={project.demo}
                target="_blank"
                rel="noreferrer"
                className="hover:text-teal-400 transition-colors"
              >
                {project.name}
              </a>
            </h4>
            <p className="text-sm text-gray-400 mt-2 leading-relaxed flex-1">
              {project.desc}
            </p>

            <div className="flex flex-wrap gap-2 mt-4">
              {project.tech.map((tech) => (
                <span
                  key={tech}
                  className="text-xs text-teal-400 font-medium bg-gray-800 rounded px-2 py-0.5"
                >
                  {tech}
                </span>
              ))}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
